import React, { useState } from "react";
import { useBetSlip } from "../providers/BetSlipProvider";
import { Currency } from "../types/enums";
import { useI18n } from "../providers/I18nProvider";

interface SuccessPlaceBetModalProps {
  orderNumber: string;
  barcode: string;
  stake: number;
  totalOdds: number;
  maxPayout: number;
  totalBonus?: number;
  bonusPercent?: number;
  totalTax?: number;
  taxPercentage?: number;
  vatValue?: number;
  vatPercentage?: number;
  netStake?: number;
  currency?: string;
  onContinue: () => void;
  onReuseSelections: () => void;
}

const formatNumber = (value: number): string =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const SuccessPlaceBetModal: React.FC<SuccessPlaceBetModalProps> = ({
  orderNumber,
  barcode,
  stake,
  totalOdds,
  maxPayout,
  totalBonus,
  bonusPercent,
  totalTax,
  taxPercentage,
  vatValue = 0,
  vatPercentage = 0,
  netStake,
  currency = Currency.UGX,
  onContinue,
  onReuseSelections,
}) => {
  const showVat = vatValue > 0 && vatPercentage > 0;
  const { config, betSlipState } = useBetSlip();
  const hasBetBuilderBets = betSlipState.betBuilderState.bets.length > 0;
  const { t, tHtml } = useI18n();
  const [copied, setCopied] = useState(false);
  const sharedText =
    "Hey! Click here to see my sport selections at " + config.brand.name;
  const sharedLink =
    window.location.origin + "/sportsbook/betslip" + "?loadBet=" + barcode;
  const shareLinks = [
    {
      name: "WhatsApp",
      href: `https://wa.me/?text=${sharedText}: ${sharedLink}`,
      icon: (
        <i
          style={{ backgroundImage: "url('/assets/share-whatsapp.svg')" }}
          className="au-m-ico-c w-full h-full"
        ></i>
      ),
      color: "text-green-500",
    },
    {
      name: "Telegram",
      href: `https://telegram.me/share/url?url=${sharedText}&text=${sharedLink}`,
      icon: (
        <i
          style={{ backgroundImage: "url('/assets/share-telegram.svg')" }}
          className="au-m-ico-c w-full h-full"
        ></i>
      ),
      color: "text-blue-500",
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${sharedLink}&quote=${sharedText}`,
      icon: (
        <i
          style={{ backgroundImage: "url('/assets/share-facebook.svg')" }}
          className="au-m-ico-c w-full h-full"
        ></i>
      ),
      color: "text-blue-600",
    },
    {
      name: "Copy Link",
      icon: (
        <i
          style={{ backgroundImage: "url('/assets/share-link.svg')" }}
          className="au-m-ico-c w-full h-full"
        ></i>
      ),
      color: "text-gray-600",
    },
  ];

  return (
    <div className="px-4 pb-3 pt-6">
      {/* Success Message */}
      <div className="text-center mb-6">
        <div className="text-25 mb-4">{t("BetSlipBetPlacedExplanation")}</div>
        <div
          className="mb-6 text-25 font-bold"
          dangerouslySetInnerHTML={tHtml("BetSlipBetPlacedID", {
            betId: orderNumber,
          })}
        />
      </div>

      {/* Bet Details */}
      <div className="bg-modal-betslip-summary-bg rounded-2 p-4 mb-6 space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm">{t("BetSlipComboStake")}</span>
          <span className="text-sm font-semibold text-gray-900">
            {formatNumber(stake)} {currency}
          </span>
        </div>
        {showVat && (
          <div
            className="flex justify-between items-center"
            dangerouslySetInnerHTML={tHtml("BetSlipExciseTax", {
              tax: vatPercentage,
              taxValue: formatNumber(vatValue),
              currency,
            })}
          />
        )}
        {showVat && netStake !== undefined && (
          <div
            className="flex justify-between items-center"
            dangerouslySetInnerHTML={tHtml("BetSlipNetStake", {
              receiptNetStake: formatNumber(netStake),
              currency,
            })}
          />
        )}
        {totalOdds > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-sm">
              {t("BetSlipTotalOdds", { "totalOdds:span": "" }).trim()}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {formatNumber(totalOdds)}
            </span>
          </div>
        )}
        {totalBonus !== undefined && totalBonus > 0 && (
          <div
            className="flex justify-between items-center"
            dangerouslySetInnerHTML={tHtml("BetSlipBonusBoost", {
              bonusPercent: bonusPercent ?? 0,
              bonusValue: formatNumber(totalBonus),
              currency,
            })}
          />
        )}
        {totalTax !== undefined && totalTax !== 0 && (
          <div
            className="flex justify-between items-center"
            dangerouslySetInnerHTML={tHtml(
              (taxPercentage ?? 0) > 0 ? "TaxPercentage" : "NoTaxPercentage",
              {
                tax: ((taxPercentage ?? 0) * 100).toFixed(0),
                taxValue: `${totalTax < 0 ? "-" : ""}${formatNumber(Math.abs(totalTax))}`,
                currency,
              },
            )}
          />
        )}
        <div
          className="flex justify-between font-semibold [&_.maxPayout]:flex [&_.maxPayout]:w-full [&_.maxPayout]:justify-between"
          dangerouslySetInnerHTML={tHtml("BetSlipMaxPayOut", {
            maxPayOut: formatNumber(maxPayout),
            currency,
          })}
        />
      </div>

      {/* Share Section */}
      {!hasBetBuilderBets && (
        <div className="mb-9">
          <p className="text-center text-gray-800 text-md mb-5">
            {t("BetslipBetPlacedModalTitle")}
          </p>
          <div className="flex justify-center gap-9">
            {shareLinks.map((link) => (
              <button
                key={link.name}
                className={`rounded-full w-9 h-9 ${link.color}`}
                aria-label={`Share via ${link.name}`}
                onClick={() =>
                  link.href
                    ? window.open(link.href, "_blank", "noopener,noreferrer")
                    : (navigator.clipboard.writeText(sharedLink),
                      setCopied(true),
                      setTimeout(() => setCopied(false), 500))
                }
              >
                {!link.href && copied ? "Link Copied!" : link.icon}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={onContinue}
          className="w-full px-6 text-22 h-[45px] bg-btn-continue-betting text-btn-continue-betting-text rounded-4"
        >
          {t("BetSlipBetSuccessContinueBetting")}
        </button>
        <button
          onClick={onReuseSelections}
          className="w-full px-6 text-22 h-[45px] bg-transparent border border-btn-reuse-selections-border text-btn-reuse-selections-text rounded-4"
        >
          {t("BetSlipBetSuccessRebet")}
        </button>
      </div>
    </div>
  );
};

export default SuccessPlaceBetModal;
